import { recordOutreach } from './outreach';
// Records a call, text, or email that the agent started from a transaction task, so the team can look it up later.
// Resi can't see whether a text or call actually went through: this records that it was opened and sent from Resi.
import { ref, push, set, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { database } from '../firebaseConfig';

// Firebase rejects undefined values, so drop them before saving.
const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const logContact = async (transactionId, entry) => {
  if (!transactionId) return;
  try {
    const user = getAuth().currentUser;
    const record = clean({
      kind: entry.kind,                      // 'call' | 'text' | 'email'
      toName: entry.toName,
      toPhone: entry.toPhone,
      toEmail: entry.toEmail,
      role: entry.role,
      subject: entry.subject,
      body: entry.body,
      taskName: entry.taskName,
      status: entry.kind === 'call' ? 'Call started from Resi' : 'Opened in phone or email app from Resi',
      sentAt: new Date().toISOString(),
      byName: user?.displayName || user?.email || '',
      byUid: user?.uid || '',
    });
    await set(push(ref(database, `transactions/${transactionId}/communications`)), record);
    if (user?.uid) await recordOutreach(user.uid, entry.kind, 'transaction', { transactionId });
    // Client calls/texts/emails also go on the client's lead/contact record (outreach is already counted above).
    if (entry.role === 'Client') await logOnClientRecord(transactionId, entry, record).catch((err) => console.error('Could not log on client record', err));
  } catch (err) {
    console.error('Could not log contact', err);
  }
};

// Finds the deal's linked CRM contacts that match the number or email used, and adds the entry to their timeline.
const digits10 = (p) => String(p || '').replace(/\D/g, '').slice(-10);
export const clientContactIdsFor = (contacts, { phone, email }) => {
  const d = digits10(phone); const e = String(email || '').trim().toLowerCase();
  return Object.entries(contacts || {}).filter(([, c]) => c && ((d.length === 10 && [c.phone, c.mobile, c.phone2].some((x) => digits10(x) === d))
    || (e && String(c.email || '').trim().toLowerCase() === e))).map(([id]) => id);
};
const logOnClientRecord = async (transactionId, entry, record) => {
  const txSnap = await get(ref(database, `transactions/${transactionId}`));
  const tx = txSnap.exists() ? txSnap.val() : {};
  const owner = tx.ownerUid || tx.createdBy || record.byUid;
  let ids = Object.keys(tx.contactIds || {});
  if (!ids.length && owner) { const s = await get(ref(database, `users/${owner}/transactions/${transactionId}/contactIds`)).catch(() => null); ids = s && s.exists() ? Object.keys(s.val()) : []; }
  const contacts = {};
  for (const id of ids) { const s = await get(ref(database, `crm/contacts/${id}`)).catch(() => null); if (s && s.exists()) contacts[id] = s.val(); }
  const match = clientContactIdsFor(contacts, { phone: entry.toPhone, email: entry.toEmail });
  const kind = entry.kind === 'call' ? 'call_attempt' : entry.kind;
  const snippet = String(entry.body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
  const text = entry.kind === 'call' ? `Call started from deal task${entry.taskName ? ` (${entry.taskName})` : ''}`
    : entry.kind === 'email' ? `Email from deal task: ${entry.subject || '(no subject)'}` : `Text from deal task${snippet ? `: ${snippet}` : ''}`;
  for (const id of match) {
    await set(push(ref(database, `crm/activity/${id}`)), clean({ type: kind, direction: 'outbound', via: entry.via || (entry.kind === 'call' ? 'tel' : 'device'), number: entry.toPhone ? String(entry.toPhone).replace(/[^0-9+]/g, '') : undefined,
      subject: entry.subject, snippet: snippet || undefined, agentName: record.byName, by: record.byUid || null, transactionId, at: record.sentAt, text }));
  }
};
